<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, reactive, ref, watch } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import {
  LibraryStepViewDataQuery,
  LibraryStepViewDataQueryVariables,
  LibraryStepViewDeleteMutation,
  LibraryStepViewDeleteMutationVariables,
  LibraryStepViewDraftMutation,
  LibraryStepViewDraftMutationVariables,
  LibraryStepViewUpdateMutation,
  LibraryStepViewUpdateMutationVariables,
  PermissionOrganization,
  UpdateStep,
  VersionStatus,
} from '@/generated/graphql'
import gql from 'graphql-tag'
import { useRoute, useRouter } from 'vue-router'
import useVuelidate from '@vuelidate/core'
import Breadcrumb from '@/components/Breadcrumb.vue'
import WhereUsedPanel from '@/components/step/WhereUsedPanel.vue'
import StepPreviewDialog from '@/components/step/StepPreviewDialog.vue'
import TextField from '@/components/input/TextField.vue'
import TextAreaField from '@/components/input/TextAreaField.vue'
import StepTypePicker from '@/components/input/StepTypePicker.vue'
import StepWarningField from '@/components/step/StepWarningField.vue'
import StepIconsField from '@/components/step/StepIconsField.vue'
import DurationField from '@/components/input/DurationField.vue'
import StepUploadList from '@/components/stepUpload/StepUploadList.vue'
import ArticleAllocationList from '@/components/article/ArticleAllocationList.vue'
import StepCheckConfigurations from '@/components/checkConfiguration/StepCheckConfigurations.vue'
import NotificationSaved from '@/components/notifications/NotificationSaved.vue'
import DataTable from '@/components/dataTable/DataTable.vue'
import DataTableCreateButton from '@/components/dataTable/DataTableCreateButton.vue'
import ButtonDelete from '@/components/button/ButtonDelete.vue'
import AsyncDeleteDialog from '@/components/dialogs/AsyncDeleteDialog.vue'
import DateTime from '@/components/DateTime.vue'
import LibraryStepReleaseConfirmation from '@/components/step/LibraryStepReleaseConfirmation.vue'
import { ComponentExposed } from 'vue-component-type-helpers'
import { scrollToFirstInvalidField } from '@/validation'
import { createStepValidationRules } from '@/validation/step'
import useAuthorization from '@/composables/useAuthorization'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { hasOrgAuthz } = useAuthorization()
const stepId = computed(() => route.params.stepId as string)
const canWrite = computed(() => hasOrgAuthz(PermissionOrganization.OrgProductWrite))
const canDelete = computed(() => hasOrgAuthz(PermissionOrganization.OrgProductDelete))

const fetchQuery = useQuery<LibraryStepViewDataQuery, LibraryStepViewDataQueryVariables>(
  gql`
    query LibraryStepViewData($id: ID!) {
      product {
        step(id: $id) {
          id
          name
          instructionText
          warningText
          durationInSeconds
          toolIds
          stepType {
            id
          }
          libraryItemVersion {
            version
            versionStatus
          }
        }
        libraryStepVersions(stepId: $id) {
          id
          usageCount
          libraryItemVersion {
            version
            versionStatus
            releasedAt
          }
        }
      }
    }
  `,
  () => ({ id: stepId.value }),
)
type LibraryStepVersion = LibraryStepViewDataQuery['product']['libraryStepVersions'][number]
const step = computed(() => fetchQuery.result.value?.product.step)
const versions = computed<LibraryStepVersion[]>(
  () => fetchQuery.result.value?.product.libraryStepVersions ?? [],
)
const isReleased = computed(
  () => step.value?.libraryItemVersion?.versionStatus === VersionStatus.Released,
)
const canEdit = computed(() => canWrite.value && !isReleased.value)
const isDraft = (item: LibraryStepVersion) =>
  item.libraryItemVersion?.versionStatus !== VersionStatus.Released
// Versions come newest first.
const canCreateDraft = computed(() => !!versions.value[0] && !isDraft(versions.value[0]))

type Model = Partial<UpdateStep>
const model = reactive<Model>({})
const validation = useVuelidate<Model>(createStepValidationRules, model, { $scope: false })
const notifySaved = ref(false)
const whereUsedOpen = ref(false)
const whereUsedStepId = ref<string>()
const previewOpen = ref(false)
const previewStepId = ref<string>()
function openWhereUsed(item: LibraryStepVersion) {
  whereUsedStepId.value = item.id
  whereUsedOpen.value = true
}
function openPreview(item: LibraryStepVersion) {
  previewStepId.value = item.id
  previewOpen.value = true
}

watch(
  step,
  (step) => {
    if (!step) return
    model.id = step.id
    model.name = step.name
    model.instructionText = step.instructionText
    model.warningText = step.warningText
    model.durationInSeconds = step.durationInSeconds
    model.stepTypeId = step.stepType.id
    model.toolIds = step.toolIds
  },
  { immediate: true },
)

const updateMutation = useMutation<
  LibraryStepViewUpdateMutation,
  LibraryStepViewUpdateMutationVariables
>(gql`
  mutation LibraryStepViewUpdate($command: UpdateStep!) {
    product {
      updateStep(command: $command) {
        id
      }
    }
  }
`)
function save() {
  validation.value.$touch()
  if (validation.value.$invalid) {
    scrollToFirstInvalidField()
    return
  }
  updateMutation.mutate({ command: model as UpdateStep }).then(() => {
    notifySaved.value = true
  })
}

const draftMutation = useMutation<
  LibraryStepViewDraftMutation,
  LibraryStepViewDraftMutationVariables
>(gql`
  mutation LibraryStepViewDraft($stepId: ID!) {
    product {
      draftLibraryStep(stepId: $stepId) {
        id
      }
    }
  }
`)
function createDraft() {
  draftMutation.mutate({ stepId: stepId.value }).then((result) => {
    const draftStepId = result?.data?.product.draftLibraryStep.id
    if (draftStepId) router.push({ name: 'libraryStep', params: { stepId: draftStepId } })
  })
}

const releaseDialogOpen = ref(false)
const releaseVersion = ref<LibraryStepVersion>()
function openRelease(item: LibraryStepVersion) {
  releaseVersion.value = item
  releaseDialogOpen.value = true
}

const deleteMutation = useMutation<
  LibraryStepViewDeleteMutation,
  LibraryStepViewDeleteMutationVariables
>(gql`
  mutation LibraryStepViewDelete($stepId: ID!) {
    product {
      deleteLatestUnreleasedLibraryStepVersion(stepId: $stepId)
    }
  }
`)
const deleteDialog = ref<ComponentExposed<typeof AsyncDeleteDialog>>()
const doDelete = async (item: LibraryStepVersion) => {
  // Deleting version 1 deletes the library step, so there may be no version left to show.
  const remainingVersion = versions.value.find((v) => v.id !== item.id)
  await deleteDialog.value?.open(
    `${step.value?.name} v${item.libraryItemVersion?.version}`,
    async () => {
      await deleteMutation.mutate({ stepId: item.id })
      if (item.id !== stepId.value) await fetchQuery.refetch()
      else if (remainingVersion)
        await router.push({ name: 'libraryStep', params: { stepId: remainingVersion.id } })
      else await router.push({ name: 'library' })
    },
  )
}

const onVersionClick = (_: unknown, row: { item: LibraryStepVersion }) =>
  router.push({ name: 'libraryStep', params: { stepId: row.item.id } })
const versionRowProps = ({ item }: { item: LibraryStepVersion }) =>
  item.id === stepId.value ? { class: 'viewing-version', 'aria-current': 'true' } : {}

const versionHeaders = [
  { key: 'libraryItemVersion.version', title: t('entity.bop.field.version') },
  { key: 'libraryItemVersion.versionStatus', title: t('entity.bop.field.versionStatus') },
  { key: 'libraryItemVersion.releasedAt', title: t('view.organization.library.releasedAt') },
  { key: 'usageCount', title: t('view.organization.library.usage'), width: 100 },
  { key: 'actions', width: 170, sortable: false, align: 'end' },
]
</script>

<template>
  <breadcrumb />
  <h1 class="d-flex align-center">
    {{ model.name || t('view.organization.library.libraryStep') }}
    <v-chip v-if="step?.libraryItemVersion" size="small" class="ml-3">
      v{{ step.libraryItemVersion.version }}
      {{ t(`component.versionStatus.status.${step.libraryItemVersion.versionStatus}`) }}
    </v-chip>
  </h1>

  <step-preview-dialog
    v-if="previewStepId"
    v-model="previewOpen"
    :step-id="previewStepId"
    :draft-step-id="stepId"
    :draft="previewStepId === stepId ? model : undefined"
  />

  <h3>{{ t('view.organization.library.versions') }}</h3>
  <data-table
    :items="versions"
    :headers="versionHeaders"
    :loading="fetchQuery.loading.value"
    density="comfortable"
    :row-props="versionRowProps"
    @click:row="onVersionClick"
  >
    <template v-if="canWrite" #header.actions>
      <data-table-create-button
        :disable-reason="
          canCreateDraft ? undefined : t('view.organization.bops.cannotCreateMessage')
        "
        :loading="draftMutation.loading"
        @click="createDraft()"
      />
    </template>
    <template #item.libraryItemVersion.version="{ item }: { item: LibraryStepVersion }">
      <span :class="{ 'font-weight-bold': item.id === stepId }">
        v{{ item.libraryItemVersion?.version }}
      </span>
    </template>
    <template #item.libraryItemVersion.versionStatus="{ item }: { item: LibraryStepVersion }">
      {{ t(`component.versionStatus.status.${item.libraryItemVersion?.versionStatus}`) }}
    </template>
    <template #item.libraryItemVersion.releasedAt="{ item }: { item: LibraryStepVersion }">
      <date-time :model-value="item.libraryItemVersion?.releasedAt" format="date" />
    </template>
    <template #item.actions="{ item }: { item: LibraryStepVersion }">
      <div class="d-flex justify-end ga-2">
        <button-delete v-if="isDraft(item) && canDelete" @click="doDelete(item)" />
        <v-btn
          v-if="isDraft(item) && canWrite"
          variant="text"
          density="compact"
          icon="verified"
          :title="t('view.organization.bops.releaseTooltip')"
          @click.stop="openRelease(item)"
        />
        <v-btn
          variant="text"
          density="compact"
          icon="visibility"
          :title="t('component.stepPreviewDialog.button')"
          @click.stop="openPreview(item)"
        />
        <v-btn
          variant="text"
          density="compact"
          icon="account_tree"
          :title="t('view.organization.library.whereUsedTitle')"
          @click.stop="openWhereUsed(item)"
        />
      </div>
    </template>
  </data-table>

  <v-alert
    v-if="isReleased"
    type="info"
    variant="tonal"
    density="compact"
    border="start"
    icon="lock"
    class="mt-8"
  >
    {{ t('view.organization.library.releasedReadOnly') }}
  </v-alert>

  <v-card v-if="step" :class="isReleased ? 'mt-4' : 'mt-8'">
    <v-card-text>
      <text-field
        v-model="model.name"
        :label="t('entity.step.field.name')"
        required
        :validation="validation.name"
        :disabled="!canEdit"
      />
      <step-type-picker
        v-model="model.stepTypeId"
        :label="t('entity.step.field.stepType')"
        required
        :validation="validation.stepTypeId"
        :disabled="!canEdit"
      />
      <duration-field
        v-model="model.durationInSeconds"
        :label="t('entity.step.field.durationInSeconds')"
        required
        :validation="validation.durationInSeconds"
        :disabled="!canEdit"
      />
      <text-area-field
        v-model="model.instructionText"
        :label="t('entity.step.field.instructionText')"
        :validation="validation.instructionText"
        markdown
        :disabled="!canEdit"
      />
      <step-warning-field v-model="model.warningText" :disabled="!canEdit" />
    </v-card-text>
    <v-card-actions v-if="canEdit">
      <v-spacer />
      <v-btn
        color="primary"
        variant="elevated"
        :loading="updateMutation.loading.value"
        @click="save()"
      >
        {{ t('button.save') }}
      </v-btn>
    </v-card-actions>
  </v-card>

  <where-used-panel v-if="whereUsedStepId" v-model="whereUsedOpen" :step-id="whereUsedStepId" />

  <template v-if="step">
    <h3>{{ t('entity.article.plural') }}</h3>
    <article-allocation-list label="Parts" :step-id="stepId" :readonly="isReleased" />

    <h3>{{ t('entity.step.field.icons') }}</h3>
    <v-card>
      <v-card-text>
        <step-icons-field :step-id="stepId" :disabled="!canEdit" />
      </v-card-text>
    </v-card>

    <h3>{{ t('entity.stepUpload.plural') }}</h3>
    <step-upload-list :step-id="stepId" :readonly="isReleased" />

    <h3>{{ t('entity.checkConfiguration.plural') }}</h3>
    <step-check-configurations :step-id="stepId" :readonly="isReleased" />
  </template>

  <library-step-release-confirmation
    v-if="releaseVersion"
    v-model="releaseDialogOpen"
    :step-id="releaseVersion.id"
    @release="fetchQuery.refetch()"
  />

  <async-delete-dialog ref="deleteDialog" />

  <div class="pb-8" />

  <notification-saved v-model="notifySaved" />
</template>

<style scoped lang="scss">
h3 {
  margin-top: 2em;
  margin-bottom: 1em;
}

:deep(tr.viewing-version) {
  background: rgba(var(--v-theme-primary), 0.08);
  box-shadow: inset 4px 0 0 rgb(var(--v-theme-primary));
}
</style>
